import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import clsx from 'clsx';

import { useFeatureFlag } from '@/hooks/featureFlag/useFeatureFlag';
import { useMenu } from '@/utils/hooks/menu/useMenu';

import useToggle from '@/utils/hooks/utils/useToggle';

import type { MenuEntryType } from '@/lib/platform/menu/menu';

import Icon from '@/components/base/Icon';
import Iconosquare from '@/components/icons/Iconosquare';
import IconosquareChristmas from '@/components/icons/IconosquareChristmas';
import { MenuMobileEntries } from '@/components/layouts/LayoutPlatform/components/Menu/MenuMobile/Sidebar/components/MenuMobileEntries';
import { MenuMobileSubEntries } from '@/components/layouts/LayoutPlatform/components/Menu/MenuMobile/Sidebar/components/MenuMobileSubEntries';
import { Wrapper } from '@/components/layouts/LayoutPlatform/components/Menu/MenuMobile/Sidebar/Wrapper';

export type SideBarProps = {
    isMenuOpen: boolean;
    toggleMenu: () => void;
};

export function SideBar({ isMenuOpen, toggleMenu }: SideBarProps) {
    const showChristmasPromotion = useFeatureFlag('christmas_promotion');

    return (
        <Wrapper isMenuOpen={isMenuOpen} toggleMenu={toggleMenu}>
            <div className="mx-5 mb-3 flex items-center justify-between">
                <Link href="/" className="inline-block leading-none" data-testid="link-home-page">
                    {showChristmasPromotion ? (
                        <IconosquareChristmas />
                    ) : (
                        <Iconosquare size={24} className="text-icoBlue" />
                    )}
                </Link>

                <button
                    data-testid="button-close-social-inbox-mobile-menu"
                    onClick={toggleMenu}
                    className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                    <Icon icon="close" />
                </button>
            </div>
            <SideBarContent />
        </Wrapper>
    );
}

function SideBarContent() {
    const wrapperScrollRef = useRef<HTMLDivElement>(null);
    const { isOpen, toggle } = useToggle();
    const [activeEntry, setActiveEntry] = useState<MenuEntryType['id']>(null);

    useEffect(() => {
        if (!isOpen && activeEntry) {
            setTimeout(() => setActiveEntry(null), 300);
        }
    }, [isOpen]);

    const handleActiveEntry = (id: MenuEntryType['id']) => {
        setActiveEntry(id);
        wrapperScrollRef.current.scrollTo({ top: 0 });
        toggle();
    };

    const { mainMenu, bottomMenu } = useMenu();

    return (
        <div
            data-testid="social-inbox-mobile-menu-sidebar-content"
            ref={wrapperScrollRef}
            className="scrollbar relative flex h-full overflow-x-hidden p-5 align-top">
            <div
                className={clsx(
                    'flex w-full min-w-full flex-col justify-between gap-4 transition-all duration-300',
                    {
                        '-translate-x-56 overflow-hidden': isOpen,
                        'translate-x-0': !isOpen
                    }
                )}>
                <MenuMobileEntries
                    menuConfig={mainMenu}
                    activeEntry={activeEntry}
                    setActiveEntry={handleActiveEntry}
                />
                <MenuMobileEntries
                    menuConfig={bottomMenu}
                    activeEntry={activeEntry}
                    setActiveEntry={handleActiveEntry}
                />
            </div>
            <div
                className={clsx('flex w-full min-w-full transition-all duration-300', {
                    'translate-x-6 overflow-hidden': !isOpen,
                    '-translate-x-full': isOpen
                })}>
                <MenuMobileSubEntries activeEntry={activeEntry} toggleSubEntries={toggle} />
            </div>
        </div>
    );
}
